import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  /** Array of FAQ items to display */
  items: FAQItem[];
  /** Optional heading - defaults to "Frequently Asked Questions" */
  heading?: string;
}

/**
 * SEO-friendly FAQ accordion section.
 * Displays expandable FAQ items with proper heading hierarchy.
 * Works with FAQSchema to provide JSON-LD structured data for rich snippets.
 */
export function FAQSection({
  items,
  heading = 'Frequently Asked Questions',
}: FAQSectionProps): React.JSX.Element {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleItem = (index: number): void => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold mb-4">{heading}</h2>
      <div className="space-y-2">
        {items.map((item, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-600"
                onClick={() => toggleItem(index)}
                aria-expanded={isExpanded}
              >
                <span className="font-medium text-gray-900">{item.question}</span>
                <ChevronDown
                  className={`h-5 w-5 text-gray-500 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 bg-white">
                  <p className="text-gray-600">{item.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
